import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { navLinks, contactInfo } from "../data/mock";
import { Phone, Menu, X } from "lucide-react";
import { scrollToSection } from "../lib/scroll";

const LOGO_URL = "https://customer-assets-0z36b82j.emergentagent.net/job_chauffeur-prive-29/artifacts/ebdff0165cb41c3c_Van%20D%27Iroise.jpg";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = (e, href) => {
    e.preventDefault();
    setMobileOpen(false);
    scrollToSection(href);
  };

  return (
    <motion.header
      data-testid="main-header"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 backdrop-blur-xl ${
        scrolled
          ? "bg-[#1a1d2e]/95 border-b border-[#c9a84c]/20"
          : "bg-[#1a1d2e]/60 border-b border-[#c9a84c]/10"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="flex items-center justify-between h-[72px] md:h-[80px]">
          {/* Logo */}
          <a
            data-testid="header-logo"
            href="#accueil"
            onClick={(e) => handleNavClick(e, "#accueil")}
            className="flex items-center gap-3"
          >
            <img
              src={LOGO_URL}
              alt="Van d'Iroise - Chauffeur Privé"
              className="h-[44px] md:h-[52px] w-auto object-contain"
            />
          </a>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className="text-[14px] font-normal relative py-3 text-white/80 hover:text-[#c9a84c] transition-colors duration-200 hover-underline-link"
              >
                {link.label}
              </a>
            ))}
          </nav>

          {/* Phone CTA */}
          <div className="hidden md:flex items-center gap-3">
            <a
              data-testid="header-phone-cta"
              href={contactInfo.phoneLink}
              className="flex items-center gap-2 text-[14px] font-medium text-[#c9a84c] hover:text-[#d4af5c] transition-colors duration-200"
            >
              <Phone size={16} strokeWidth={1.5} />
              {contactInfo.phone}
            </a>
          </div>

          {/* Mobile Toggle */}
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-2 text-white transition-colors"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Menu"
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        data-testid="mobile-menu"
        className={`lg:hidden overflow-hidden transition-all duration-300 bg-[#1a1d2e] ${
          mobileOpen ? "max-h-[400px] border-b border-[#c9a84c]/20" : "max-h-0"
        }`}
      >
        <nav className="px-6 py-6 flex flex-col gap-4">
          {navLinks.map((link) => (
            <a
              key={link.href}
              data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
              href={link.href}
              onClick={(e) => handleNavClick(e, link.href)}
              className="text-[16px] text-white/80 py-2 border-b border-white/10 hover:text-[#c9a84c] transition-colors"
            >
              {link.label}
            </a>
          ))}
          <a
            href={contactInfo.phoneLink}
            className="flex items-center gap-2 text-[16px] text-[#c9a84c] py-2 font-medium"
          >
            <Phone size={18} strokeWidth={1.5} />
            {contactInfo.phone}
          </a>
        </nav>
      </div>
    </motion.header>
  );
};
